import type { DateTime } from "luxon";
import { EmploymentType, type SelfScheduling, SelfSchedulingType } from "../../../utils/models";

export const SHIFTS_PER_WEEK_BY_EMPLOYMENT_TYPE = {
	[EmploymentType.FullTime]: 3,
	[EmploymentType.PartTime]: 1,
	[EmploymentType.PRN]: 0,
	[EmploymentType.PerDiem]: 0,
	[EmploymentType.Travel]: 0,
	[EmploymentType.FloatPool]: 0,
	[EmploymentType.Unknown]: 0,
} as const satisfies Record<EmploymentType, number>;

export const calculateSchedulingPeriod = (selfScheduling: SelfScheduling, timezone: string): { periodStart: DateTime; periodEnd: DateTime } => {
	return {
		periodStart: selfScheduling.periodStart.setZone(timezone, { keepLocalTime: true }),
		periodEnd: selfScheduling.periodEnd.setZone(timezone, { keepLocalTime: true }).endOf("day"),
	};
};

export const calculateNextPeriod = (selfScheduling: SelfScheduling): { periodStart: DateTime; periodEnd: DateTime } => {
	switch (selfScheduling.type) {
		case SelfSchedulingType.Monthly: {
			// Step 1: New period starts the day after current period ends
			const newPeriodStart = selfScheduling.periodEnd.plus({ days: 1 }).startOf("day");

			// Step 2: Find the last day of the month containing newPeriodStart
			const lastDayOfMonth = newPeriodStart.endOf("month");

			// Step 3: Find first Saturday (weekday 6 in Luxon) on or after last day of month
			// If last day is already Saturday, keep it
			// Add 7 before modulo to handle Sunday (weekday 7) correctly
			const daysUntilSaturday = (((6 - lastDayOfMonth.weekday) % 7) + 7) % 7;
			const newPeriodEnd = lastDayOfMonth.plus({ days: daysUntilSaturday }).endOf("day");

			return { periodStart: newPeriodStart, periodEnd: newPeriodEnd };
		}
		default:
			throw new Error(`Unsupported scheduling type: ${selfScheduling.type}`);
	}
};

export const calculatePreviousPeriod = (selfScheduling: SelfScheduling): { periodStart: DateTime; periodEnd: DateTime } => {
	switch (selfScheduling.type) {
		case SelfSchedulingType.Monthly: {
			// Step 1: Previous period ends the day before current period starts
			const newPeriodEnd = selfScheduling.periodStart.minus({ days: 1 }).endOf("day");

			// Step 2: Try going back 1 month first
			const oneMonthBack = newPeriodEnd.minus({ months: 1 });
			const lastDayOneMonthBack = oneMonthBack.endOf("month");
			const daysUntilSaturdayTest = (((6 - lastDayOneMonthBack.weekday) % 7) + 7) % 7;
			const testEndOfPeriod = lastDayOneMonthBack.plus({ days: daysUntilSaturdayTest });

			// Step 3: Check if the calculated end would be >= newPeriodEnd
			// If so, we need to go back 2 months instead of 1
			const monthsToGoBack = testEndOfPeriod >= newPeriodEnd ? 2 : 1;

			// Step 4: Calculate with the correct number of months
			const monthBeforePreviousEnd = newPeriodEnd.minus({ months: monthsToGoBack });
			const lastDayOfDeterminingMonth = monthBeforePreviousEnd.endOf("month");

			// Step 5: Find first Saturday on or after the last day of that month
			const daysUntilSaturday = (((6 - lastDayOfDeterminingMonth.weekday) % 7) + 7) % 7;
			const endOfPrevPeriod = lastDayOfDeterminingMonth.plus({ days: daysUntilSaturday });

			// Step 6: Previous period starts the day after that Saturday
			const newPeriodStart = endOfPrevPeriod.plus({ days: 1 }).startOf("day");

			return { periodStart: newPeriodStart, periodEnd: newPeriodEnd };
		}
		default:
			throw new Error(`Unsupported scheduling type: ${selfScheduling.type}`);
	}
};
